import {
  PanelBody,
  RangeControl,
  __experimentalToggleGroupControl as ToggleGroupControl,
  __experimentalToggleGroupControlOption as ToggleGroupControlOption,
  Button,
  BaseControl,
  Modal,
  Dropdown,
  MenuGroup,
  MenuItem,
} from "@wordpress/components";
import { InspectorControls } from "@wordpress/block-editor";
import { useState } from "@wordpress/element";
import { dispatch, select, useSelect } from "@wordpress/data";
import Label from "../../../../components/Label";
import LayoutControl from "../../../../components/Layout";
import {
  alignItemsOptions,
  directionOptions,
  justifyContentOptions,
  wrapOptions,
} from "../../../../components/Layout/config.js";
import { __ } from "@wordpress/i18n";
import { plus, trash, desktop, tablet, mobile, check } from "@wordpress/icons";

import "./style.scss";

const ViewControl = ({
  label,
  help,
  options,
  attributeKey,
  setter,
  selectedValue,
}) => {
  const currentDeviceType = select("core/editor").getDeviceType() || "Desktop";
  const setDeviceType = (deviceType) => {
    dispatch("core/editor").setDeviceType(deviceType);
  };

  const deviceIcons = {
    Desktop: desktop,
    Tablet: tablet,
    Mobile: mobile,
  };

  return (
    <BaseControl help={help}>
      <Label
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span>{label}</span>
            <Dropdown
              renderToggle={({ isOpen, onToggle }) => (
                <Button
                  icon={deviceIcons[currentDeviceType]}
                  onClick={onToggle}
                  aria-expanded={isOpen}
                  style={{ padding: "2px" }}
                />
              )}
              renderContent={() => (
                <MenuGroup>
                  <MenuItem
                    icon={currentDeviceType === "Desktop" ? check : null}
                    onClick={() => setDeviceType("Desktop")}
                  >
                    {__("Desktop")}
                  </MenuItem>
                  <MenuItem
                    icon={currentDeviceType === "Tablet" ? check : null}
                    onClick={() => setDeviceType("Tablet")}
                  >
                    {__("Tablet")}
                  </MenuItem>
                  <MenuItem
                    icon={currentDeviceType === "Mobile" ? check : null}
                    onClick={() => setDeviceType("Mobile")}
                  >
                    {__("Mobile")}
                  </MenuItem>
                </MenuGroup>
              )}
            />
          </div>
        }
      />
      <LayoutControl
        options={options}
        onChange={(value) => setter(value)}
        selected={selectedValue}
      />
    </BaseControl>
  );
};

export default function ContainerInspectorControls({
  attributes,
  setAttributes,
}) {
  const {
    width,
    height,
    isChild,
    layout,
    alignItems,
    justifyContent,
    direction,
    wrap,
    columns,
    rows,
    parentLayout,
    gridTemplateColumns,
    gridTemplateRows,
    columnSpan,
    rowSpan,
  } = attributes;

  const [modalType, setModalType] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);

  const currentDeviceType = select("core/editor").getDeviceType() || "Desktop";
  const deviceKey = currentDeviceType.toLowerCase();

  // Fetch parent block to get its columns and rows
  const parentBlock = useSelect(
    (select) => {
      const parentClientId = select("core/block-editor").getBlockRootClientId(
        select("core/block-editor").getSelectedBlockClientId()
      );
      if (parentClientId) {
        const parent = select("core/block-editor").getBlock(parentClientId);
        return parent?.name === "brandy/container-block" ? parent : null;
      }
      return null;
    },
    []
  );

  const parentColumns = parentBlock?.attributes.columns?.[deviceKey] ?? parentBlock?.attributes.columns?.desktop ?? 1;
  const parentRows = parentBlock?.attributes.rows?.[deviceKey] ?? parentBlock?.attributes.rows?.desktop ?? 1;

  const currentColumns = !isChild ? (columns?.[deviceKey] ?? columns?.desktop ?? 1) : parentColumns;
  const currentRows = !isChild ? (rows?.[deviceKey] ?? rows?.desktop ?? 1) : parentRows;
  const currentColumnSpan = columnSpan?.[deviceKey] ?? columnSpan?.desktop ?? 1;
  const currentRowSpan = rowSpan?.[deviceKey] ?? rowSpan?.desktop ?? 1;



  const getValueForView = (attribute) => {
    return attribute?.[deviceKey] || attribute?.desktop || "center";
  };

  const setValueForView = (attribute, attributeKey, value) => {

    setAttributes({
      [attributeKey]: {
        ...attribute,
        [deviceKey]: value,
      },
    });
  };

  const addColumn = () => {
    const newColumns = currentColumns + 1;
    setAttributes({
      columns: { ...columns, [deviceKey]: newColumns },
      gridTemplateColumns: {
        ...gridTemplateColumns,
        [deviceKey]: [
          ...(gridTemplateColumns?.[deviceKey] || gridTemplateColumns?.desktop || []),
          "1fr",
        ],
      },
    });
  };

  const deleteColumn = (index) => {
    if (currentColumns > 1) {
      const newColumns = currentColumns - 1;
      const newGridTemplateColumns = [
        ...(gridTemplateColumns?.[deviceKey] || gridTemplateColumns?.desktop || []),
      ];
      newGridTemplateColumns.splice(index, 1);
      setAttributes({
        columns: { ...columns, [deviceKey]: newColumns },
        gridTemplateColumns: {
          ...gridTemplateColumns,
          [deviceKey]: newGridTemplateColumns,
        },
      });
    }
  };

  const addRow = () => {
    const newRows = currentRows + 1;
    setAttributes({
      rows: { ...rows, [deviceKey]: newRows },
      gridTemplateRows: {
        ...gridTemplateRows,
        [deviceKey]: [
          ...(gridTemplateRows?.[deviceKey] || gridTemplateRows?.desktop || []),
          "1fr",
        ],
      },
    });
  };

  const deleteRow = (index) => {
    if (currentRows > 1) {
      const newRows = currentRows - 1;
      const newGridTemplateRows = [
        ...(gridTemplateRows?.[deviceKey] || gridTemplateRows?.desktop || []),
      ];
      newGridTemplateRows.splice(index, 1);
      setAttributes({
        rows: { ...rows, [deviceKey]: newRows },
        gridTemplateRows: {
          ...gridTemplateRows,
          [deviceKey]: newGridTemplateRows,
        },
      });
    }
  };

  const parseGridValue = (value) => {
    if (typeof value === "string") {
      const num = parseFloat(value);
      return isNaN(num) ? 1 : num;
    }
    return value;
  };

  const openModal = (type, index) => {
    setModalType(type);
    setModalIndex(index);
  };

  const closeModal = () => {
    setModalType(null);
    setModalIndex(null);
  };

  const deviceIcons = {
    Desktop: desktop,
    Tablet: tablet,
    Mobile: mobile,
  };

  return (
    <InspectorControls>
      {/* General Settings */}
      <PanelBody title={__("General Setting")} initialOpen={true}>
        {isChild && parentLayout !== "grid" && (
          <BaseControl>
            <Label
              title={
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span>{__("Width (%)")}</span>
                  <Dropdown
                    renderToggle={({ isOpen, onToggle }) => (
                      <Button
                        icon={deviceIcons[currentDeviceType]}
                        onClick={onToggle}
                        aria-expanded={isOpen}
                        style={{ padding: "2px" }}
                      />
                    )}
                    renderContent={() => (
                      <MenuGroup>
                        <MenuItem
                          icon={currentDeviceType === "Desktop" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                        >
                          {__("Desktop")}
                        </MenuItem>
                        <MenuItem
                          icon={currentDeviceType === "Tablet" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                        >
                          {__("Tablet")}
                        </MenuItem>
                        <MenuItem
                          icon={currentDeviceType === "Mobile" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                        >
                          {__("Mobile")}
                        </MenuItem>
                      </MenuGroup>
                    )}
                  />
                </div>
              }
            />
            <RangeControl
              value={width?.[deviceKey] ?? width?.desktop ?? 100}
              onChange={(newWidth) => setValueForView(width, "width", newWidth)}
              min={10}
              max={100}
              step={1}
            />
          </BaseControl>
        )}
        {(!isChild || (isChild && parentLayout === "flex")) && (
          <BaseControl>
            <Label
              title={
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <span>{__("Height (px)")}</span>
                  <Dropdown
                    renderToggle={({ isOpen, onToggle }) => (
                      <Button
                        icon={deviceIcons[currentDeviceType]}
                        onClick={onToggle}
                        aria-expanded={isOpen}
                        style={{ padding: "2px" }}
                      />
                    )}
                    renderContent={() => (
                      <MenuGroup>
                        <MenuItem
                          icon={currentDeviceType === "Desktop" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                        >
                          {__("Desktop")}
                        </MenuItem>
                        <MenuItem
                          icon={currentDeviceType === "Tablet" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                        >
                          {__("Tablet")}
                        </MenuItem>
                        <MenuItem
                          icon={currentDeviceType === "Mobile" ? check : null}
                          onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                        >
                          {__("Mobile")}
                        </MenuItem>
                      </MenuGroup>
                    )}
                  />
                </div>
              }
            />
            <RangeControl
              value={height?.[deviceKey] ?? height?.desktop ?? null}
              onChange={(newHeight) => setValueForView(height, "height", newHeight)}
              min={0}
              max={1000}
              step={1}
              allowReset
            />
          </BaseControl>
        )}
      </PanelBody>
      {/* Layout Settings */}
      <PanelBody title={__("Layout Setting")} initialOpen={true}>
        <div className="brandy-container-block-settings">
          <ToggleGroupControl
            label={__("Type")}
            value={layout}
            isBlock
            __nextHasNoMarginBottom
            onChange={(newLayout) => setAttributes({ layout: newLayout })}
          >
            <ToggleGroupControlOption value="flex" label={__("Flex")} />
            <ToggleGroupControlOption value="grid" label={__("Grid")} />
          </ToggleGroupControl>
        </div>

        {layout === "flex" && (
          <>
            <div className="brandy-container-block-settings">
              <ViewControl
                label={__("Direction")}
                help="Define the direction in which blocks inside this container will be placed one after the other."
                options={directionOptions}
                attributeKey="direction"
                setter={(value) => setValueForView(direction, "direction", value)}
                selectedValue={getValueForView(direction)}
              />
            </div>
            <div className="brandy-container-block-settings">
              <ViewControl
                label={__("Wrap")}
                help="Control how items wrap within the container."
                options={wrapOptions}
                attributeKey="wrap"
                setter={(value) => setValueForView(wrap, "wrap", value)}
                selectedValue={getValueForView(wrap)}
              />
            </div>
            <div className="brandy-container-block-settings">
              <ViewControl
                label={__("Align Items")}
                help="Control how items are aligned along the cross axis."
                options={alignItemsOptions}
                attributeKey="alignItems"
                setter={(value) => setValueForView(alignItems, "alignItems", value)}
                selectedValue={getValueForView(alignItems)}
              />
            </div>
            <div className="brandy-container-block-settings">
              <ViewControl
                label={__("Justify Content")}
                help="Control how items are distributed along the main axis."
                options={justifyContentOptions}
                attributeKey="justifyContent"
                setter={(value) => setValueForView(justifyContent, "justifyContent", value)}
                selectedValue={getValueForView(justifyContent)}
              />
            </div>
          </>
        )}

        {/* Hiển thị Columns và Rows controls khi layout là grid, bất kể là parent hay child */}
        {layout === "grid" && (
          <div className="brandy-grid-controls">
            <div className="brandy-grid-wrapper-controls">
              <div className="brandy-grid-dimension-control">
                <Label
                  title={
                    <div className="brandy-grid-dimension-label">
                      <span>{__("Columns")}</span>
                      <Dropdown
                        renderToggle={({ isOpen, onToggle }) => (
                          <Button
                            icon={deviceIcons[currentDeviceType]}
                            onClick={onToggle}
                            aria-expanded={isOpen}
                            style={{ padding: "2px" }}
                          />
                        )}
                        renderContent={() => (
                          <MenuGroup>
                            <MenuItem
                              icon={currentDeviceType === "Desktop" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                            >
                              {__("Desktop")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Tablet" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                            >
                              {__("Tablet")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Mobile" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                            >
                              {__("Mobile")}
                            </MenuItem>
                          </MenuGroup>
                        )}
                      />
                    </div>
                  }
                />
                <div className="brandy-action-buttons">
                  <Button
                    className="brandy-action-button"
                    icon={plus}
                    onClick={addColumn}
                    isSecondary
                    disabled={currentColumns >= 12}
                  >
                    {__("Add")}
                  </Button>
                </div>
              </div>
              <div className="brandy-grid-items-list">
                {(gridTemplateColumns?.[deviceKey] || gridTemplateColumns?.desktop || []).map(
                  (col, index) => (
                    <div key={index} className="brandy-grid-button">
                      <Button
                        onClick={() => openModal("columns", index)}
                        style={{ flex: 1, textAlign: "left" }}
                      >
                        <span>
                          {__("Column")} {index + 1}: {col}
                        </span>
                      </Button>
                      <Button
                        icon={trash}
                        onClick={() => deleteColumn(index)}
                        isDestructive
                        disabled={currentColumns <= 1}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
            <div className="brandy-grid-wrapper-controls">
              <div className="brandy-grid-dimension-control">
                <Label
                  title={
                    <div className="brandy-grid-dimension-label">
                      <span>{__("Rows")}</span>
                      <Dropdown
                        renderToggle={({ isOpen, onToggle }) => (
                          <Button
                            icon={deviceIcons[currentDeviceType]}
                            onClick={onToggle}
                            aria-expanded={isOpen}
                            style={{ padding: "2px" }}
                          />
                        )}
                        renderContent={() => (
                          <MenuGroup>
                            <MenuItem
                              icon={currentDeviceType === "Desktop" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                            >
                              {__("Desktop")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Tablet" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                            >
                              {__("Tablet")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Mobile" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                            >
                              {__("Mobile")}
                            </MenuItem>
                          </MenuGroup>
                        )}
                      />
                    </div>
                  }
                />
                <div className="brandy-action-buttons">
                  <Button
                    className="brandy-action-button"
                    icon={plus}
                    onClick={addRow}
                    isSecondary
                    disabled={currentRows >= 12}
                  >
                    {__("Add")}
                  </Button>
                </div>
              </div>
              <div className="brandy-grid-items-list">
                {(gridTemplateRows?.[deviceKey] || gridTemplateRows?.desktop || []).map(
                  (row, index) => (
                    <div key={index} className="brandy-grid-button">
                      <Button
                        onClick={() => openModal("rows", index)}
                        style={{ flex: 1, textAlign: "left" }}
                      >
                        <span>
                          {__("Row")} {index + 1}: {row}
                        </span>
                      </Button>
                      <Button
                        icon={trash}
                        onClick={() => deleteRow(index)}
                        isDestructive
                        disabled={currentRows <= 1}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
            {modalType && modalIndex !== null && (
              <Modal
                title={
                  modalType === "columns"
                    ? `${__("Column")} ${modalIndex + 1} ${__("Width")}`
                    : `${__("Row")} ${modalIndex + 1} ${__("Height")}`
                }
                onRequestClose={closeModal}
                className="brandy-modal"
              >
                <RangeControl
                  label={
                    modalType === "columns"
                      ? __("Column Width (FR)")
                      : __("Row Height (FR)")
                  }
                  value={parseGridValue(
                    modalType === "columns"
                      ? (gridTemplateColumns?.[deviceKey] || gridTemplateColumns?.desktop || [])[
                          modalIndex
                        ]
                      : (gridTemplateRows?.[deviceKey] || gridTemplateRows?.desktop || [])[
                          modalIndex
                        ]
                  )}
                  onChange={(value) => {
                    if (modalType === "columns") {
                      const newColumns = [
                        ...(gridTemplateColumns?.[deviceKey] || gridTemplateColumns?.desktop || []),
                      ];
                      newColumns[modalIndex] = `${value}fr`;
                      setAttributes({
                        gridTemplateColumns: {
                          ...gridTemplateColumns,
                          [deviceKey]: newColumns,
                        },
                      });
                    } else {
                      const newRows = [
                        ...(gridTemplateRows?.[deviceKey] || gridTemplateRows?.desktop || []),
                      ];
                      newRows[modalIndex] = `${value}fr`;
                      setAttributes({
                        gridTemplateRows: {
                          ...gridTemplateRows,
                          [deviceKey]: newRows,
                        },
                      });
                    }
                  }}
                  min={1}
                  max={12}
                  step={0.5}
                />
                <Button isPrimary onClick={closeModal}>
                  {__("Done")}
                </Button>
              </Modal>
            )}
          </div>
        )}

        {isChild && parentLayout === "grid" && (
          <div className="brandy-grid-controls">
            <div className="brandy-grid-wrapper-controls">
              <div className="brandy-grid-dimension-control">
                <Label
                  title={
                    <div className="brandy-grid-dimension-label">
                      <span>{__("Column Span")}</span>
                      <Dropdown
                        renderToggle={({ isOpen, onToggle }) => (
                          <Button
                            icon={deviceIcons[currentDeviceType]}
                            onClick={onToggle}
                            aria-expanded={isOpen}
                            style={{ padding: "2px" }}
                          />
                        )}
                        renderContent={() => (
                          <MenuGroup>
                            <MenuItem
                              icon={currentDeviceType === "Desktop" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                            >
                              {__("Desktop")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Tablet" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                            >
                              {__("Tablet")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Mobile" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                            >
                              {__("Mobile")}
                            </MenuItem>
                          </MenuGroup>
                        )}
                      />
                    </div>
                  }
                />
              </div>
              <div className="brandy-grid-items-list">
                <RangeControl
                  label={__("Span Columns")}
                  value={currentColumnSpan}
                  onChange={(value) => setValueForView(columnSpan, "columnSpan", value)}
                  min={1}
                  max={parentColumns}
                  step={1}
                />
              </div>
            </div>
            <div className="brandy-grid-wrapper-controls">
              <div className="brandy-grid-dimension-control">
                <Label
                  title={
                    <div className="brandy-grid-dimension-label">
                      <span>{__("Row Span")}</span>
                      <Dropdown
                        renderToggle={({ isOpen, onToggle }) => (
                          <Button
                            icon={deviceIcons[currentDeviceType]}
                            onClick={onToggle}
                            aria-expanded={isOpen}
                            style={{ padding: "2px" }}
                          />
                        )}
                        renderContent={() => (
                          <MenuGroup>
                            <MenuItem
                              icon={currentDeviceType === "Desktop" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Desktop")}
                            >
                              {__("Desktop")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Tablet" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Tablet")}
                            >
                              {__("Tablet")}
                            </MenuItem>
                            <MenuItem
                              icon={currentDeviceType === "Mobile" ? check : null}
                              onClick={() => dispatch("core/editor").setDeviceType("Mobile")}
                            >
                              {__("Mobile")}
                            </MenuItem>
                          </MenuGroup>
                        )}
                      />
                    </div>
                  }
                />
              </div>
              <div className="brandy-grid-items-list">
                <RangeControl
                  label={__("Span Rows")}
                  value={currentRowSpan}
                  onChange={(value) => setValueForView(rowSpan, "rowSpan", value)}
                  min={1}
                  max={parentRows}
                  step={1}
                />
              </div>
            </div>
          </div>
        )}
      </PanelBody>
    </InspectorControls>
  );
}